import { useMemo, useState } from "react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  MegaphoneIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { usePublicAnnouncements } from "../hooks/usePublicAnnouncements";
import { shouldShowAnnouncementsNotice } from "../data/announcementPublic";

/**
 * Site-wide notice for Promang company announcements marked "Offentlig på
 * nettsiden" (public audience). Mirrors the floating card Promang itself
 * renders at /public/companies/:id/announcements — same idea (dismiss +
 * cycle through several messages), restyled with Airon's own design tokens.
 * Hidden while loading and whenever there is nothing live to show — no
 * banner is better than a stale or empty one.
 */
export function AnnouncementsNotice() {
  const { items, loading } = usePublicAnnouncements();
  const [dismissedIds, setDismissedIds] = useState([]);
  const [index, setIndex] = useState(0);

  const visible = useMemo(
    () => items.filter((item) => !dismissedIds.includes(item.id)),
    [items, dismissedIds]
  );

  if (!shouldShowAnnouncementsNotice({ loading, items: visible })) return null;

  const activeIndex = Math.min(index, visible.length - 1);
  const current = visible[activeIndex];
  const hasMultiple = visible.length > 1;

  const dismiss = () => {
    setDismissedIds((old) => [...old, current.id]);
    setIndex(0);
  };

  return (
    <div
      role="region"
      aria-label="Melding fra Airon"
      style={{
        position: "fixed",
        top: "max(16px, env(safe-area-inset-top))",
        left: 16,
        right: 16,
        zIndex: 1900,
        display: "flex",
        justifyContent: "center",
        pointerEvents: "none",
      }}
    >
      <div
        style={{
          pointerEvents: "auto",
          width: "100%",
          maxWidth: 560,
          background: "#fff",
          borderRadius: 16,
          border: "1px solid rgba(14,34,51,0.08)",
          borderLeft: "4px solid var(--airon)",
          padding: "16px 16px 16px 18px",
          display: "flex",
          gap: 14,
          alignItems: "flex-start",
          boxShadow: "0 16px 40px rgba(14,34,51,0.18)",
        }}
      >
        <MegaphoneIcon
          style={{ width: 22, height: 22, color: "var(--airon)", flexShrink: 0, marginTop: 2 }}
          aria-hidden="true"
        />
        <div style={{ minWidth: 0, flex: 1 }} aria-live="polite" aria-atomic="true">
          <strong style={{ display: "block", fontSize: 14, color: "var(--ink)", overflowWrap: "anywhere" }}>
            {current.title}
          </strong>
          <p
            style={{
              margin: "4px 0 0",
              fontSize: 13,
              lineHeight: 1.5,
              color: "#41586b",
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
              maxHeight: "10rem",
              overflowY: "auto",
            }}
          >
            {current.body}
          </p>
          {hasMultiple && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginTop: 10,
                borderTop: "1px solid rgba(14,34,51,0.08)",
                paddingTop: 8,
              }}
            >
              <span style={{ fontFamily: "var(--mono)", fontSize: 11, color: "#8da5bc" }}>
                {activeIndex + 1} / {visible.length}
              </span>
              <button
                type="button"
                onClick={() => setIndex((i) => Math.max(0, i - 1))}
                disabled={activeIndex === 0}
                aria-label="Forrige melding"
                style={iconButtonStyle}
              >
                <ChevronLeftIcon style={{ width: 16, height: 16 }} aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => setIndex((i) => Math.min(visible.length - 1, i + 1))}
                disabled={activeIndex === visible.length - 1}
                aria-label="Neste melding"
                style={iconButtonStyle}
              >
                <ChevronRightIcon style={{ width: 16, height: 16 }} aria-hidden="true" />
              </button>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Lukk melding"
          style={{ ...iconButtonStyle, flexShrink: 0 }}
        >
          <XMarkIcon style={{ width: 18, height: 18 }} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

const iconButtonStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 32,
  height: 32,
  border: 0,
  borderRadius: 8,
  background: "transparent",
  color: "var(--ink)",
  cursor: "pointer",
};
